<template>
  <div class="content mb-5">
    <h2>{{ $t("OTG Install Key") }}</h2>
    <p>
      {{
        $t(
          "Use the install key when installing OTG v2 on a client device such as a laptop. This key can be regenerated if it has been compromised."
        )
      }}<br />
      {{
        $t(
          "Once regenerated, the old key will not work, and you will need to use the new one for future installs."
        )
      }}
    </p>
    <b-row>
      <b-col sm="12" md="6" lg="6">
        <b-form-group
          v-if="!loading"
          class="mb-3"
          label-for="name"
          :label="this.$t('OTG Install Key')"
        >
          <b-form-input
            data-test-id="device-configuration-name"
            id="otgInstallKey"
            v-model="selectedUserAccount.install_key"
            readonly
          />
        </b-form-group>
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
      </b-col>
    </b-row>
    <b-row class="mb-6">
      <b-col cols="12" class="d-flex justify-content-end">
        <b-button
          data-test-id="device-configuration-regenerate"
          @click="onRegenerate"
          variant="secondary"
          class="mr-3"
          :disabled="fetching.otgKey"
          >{{ $t("Regenerate") }}</b-button
        >
        <b-button
          data-test-id="device-configuration-copy"
          @click="copyInstallKey"
          variant="secondary"
          :disabled="fetching.otgKey"
          >{{ $t("Copy") }}</b-button
        >
      </b-col>
    </b-row>

    <h2>{{ $t("Policy") }}</h2>
    <p>
      {{
        $t(
          "Select a specific policy, or the default policy, to automatically assign to new devices"
        )
      }}
    </p>
    <b-row class="mb-4">
      <b-col cols="12" md="6">
        <b-form-group
          id="policy"
          :label="$t('Policy')"
          label-for="policy"
          v-if="!loading"
        >
          <section class="dropDownMenuWrapperPolicy">
            <button
              :class="'dropDownMenuButton'"
              ref="menu"
              @click.stop.prevent="openDropdownPolicy()"
            >
              <span> {{ selectedPolicy.text }}</span>
            </button>

            <div class="iconWrapper">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                class="bi bi-chevron-expand"
                viewBox="0 0 16 16"
              >
                <path
                  fill-rule="evenodd"
                  d="M3.646 9.146a.5.5 0 0 1 .708 0L8 12.793l3.646-3.647a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 0-.708zm0-2.292a.5.5 0 0 0 .708 0L8 3.207l3.646 3.647a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 0 0 0 .708z"
                />
              </svg>
            </div>
            <section
              class="dropdownMenu"
              @scroll="scrollPolicy"
              infinite-scroll-disabled="busy"
              infinite-scroll-distance="limit"
              v-if="isOpenPolicy"
            >
              <div class="dropdownMenu__list">
                <div
                  class="item"
                  v-for="(policy, idx) in policiesList"
                  :key="idx"
                >
                  <div class="icon-wrap" v-if="policy.isSelected">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      class="bi bi-check-lg"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"
                      />
                    </svg>
                  </div>

                  <button
                    :class="
                      policy.isSelected
                        ? 'isSelected'
                        : 'notSelect' && policy.disabled
                        ? 'disabled'
                        : ''
                    "
                    @click.prevent="changePolicy(policy.value)"
                  >
                    {{ policy.text }}
                  </button>
                </div>
              </div>

              <!-- <slot /> -->
            </section>
          </section>
          <!--          <b-form-select-->
          <!--            data-test-id="device-configuration-policy"-->
          <!--            v-model="form.policyid"-->
          <!--            @change="setValidationInput('policyid', $event)"-->
          <!--            :options="policyOptions"-->
          <!--          ></b-form-select>-->
        </b-form-group>
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
      </b-col>
    </b-row>

    <h2>{{ $t("Device Exception") }}</h2>
    <p>
      {{
        $t("Select a device exception to automatically assign to new devices")
      }}
    </p>
    <b-row>
      <b-col cols="12" md="6">
        <b-form-group
          id="device-exception"
          :label="$t('Device Exceptions')"
          label-for="device-exception"
          v-if="!loading"
        >
          <b-form-select
            data-test-id="device-exception"
            v-model="form.device_exception_id"
            @change="setValidationInput('device_exception_id', $event)"
            :options="deviceExceptionsOptions"
          ></b-form-select>
        </b-form-group>
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
      </b-col>
    </b-row>

    <b-row class="mt-1">
      <b-col>
        <t-collapse
          :title="$t('JSON Configuration (Default for Devices)')"
          class="mb-0"
        >
          <b-alert show variant="info">
            <b-icon icon="info-circle" class="mr-3"></b-icon
            >{{
              $t(
                "Device Exceptions take precedence over filters added using JSON. If a device exception is applied to a device, any filters entered below using JSON are ignored."
              )
            }}
          </b-alert>
          <p>
            {{
              $t(
                "Use the template to configure settings that affect all devices belonging to your account. It is possible to undo your configuration changes, and reset this template to the system default using the button provided. This feature is recommended for advanced users only."
              )
            }}
          </p>
          <b-row>
            <b-col cols="12">
              <b-form-group
                v-if="!loading"
                label-for="configuration"
                :state="$validateState($v.form.configuration)"
              >
                <div class="d-flex mb-1">
                  <span
                    class="mr-3"
                    @click="enabledJsonInput = !enabledJsonInput"
                  >
                    {{ $t("Edit JSON Configuration") }}:
                  </span>
                  <b-form-checkbox
                    switch
                    name="toggle-json-input"
                    data-test-id="toggle-json-input"
                    size="lg"
                    v-model="enabledJsonInput"
                  >
                  </b-form-checkbox>
                </div>

                <b-form-textarea
                  id="configuration"
                  data-test-id="device-configuration-json"
                  v-model="$v.form.configuration.$model"
                  rows="10"
                  :state="$validateState($v.form.configuration)"
                  :disabled="!enabledJsonInput"
                />

                <template #invalid-feedback>
                  <p v-if="!$v.form.configuration.required">
                    {{ $t("This is a required field.") }}
                  </p>
                  <p
                    v-if="
                      $v.form.configuration.required &&
                      !$v.form.configuration.isValidJson
                    "
                  >
                    {{ $t("Must contain valid JSON.") }}
                  </p>
                </template>
              </b-form-group>
              <b-skeleton
                v-if="loading"
                animation="wave"
                height="250px"
                width="100%"
              ></b-skeleton>
            </b-col>
          </b-row>
          <b-row class="mb-6">
            <b-col cols="12" class="d-flex justify-content-end">
              <b-button
                @click="resetconfiguration"
                data-test-id="device-configuration-reset"
                variant="outline-secondary"
                class="border-light mr-3"
                :disabled="fetching.configuration || !enabledJsonInput"
                >{{ $t("Reset") }}</b-button
              >
              <b-button
                @click="submitConfiguration"
                data-test-id="device-configuration-save-json"
                variant="secondary"
                :disabled="
                  $v.form.configuration.$invalid ||
                  !$v.form.configuration.$anyDirty ||
                  !$v.form.configuration.isValidJson ||
                  fetching.configuration ||
                  !enabledJsonInput
                "
                >{{ $t("Save") }}</b-button
              >
            </b-col>
          </b-row>
        </t-collapse>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <t-save-button
          data-test-id="device-configuration-save-policy"
          :form-model="$v.form"
          :fetching="fetching.policy"
          :btn-text="$t('Save')"
          @submit="updateInheritedPolicy"
        />
      </b-col>
    </b-row>
  </div>
</template>
<route>
{
  "name": "Customer Device Configuration",
  "meta": {
    "authorize" : [1, 3, 4]
  }
}
</route>
<script>
import { mapActions, mapGetters } from "vuex";
import { required } from "vuelidate/lib/validators";

export default {
  layout: "customer",
  data() {
    return {
      enabledJsonInput: false,
      dataOptions: { page: 1, results: 10 },
      currentPage: null,
      previousScrollPosition: null,
      isOpenPolicy: null,
      selectedPolicy: {
        isSelected: null,
        text: "Default Policy",
      },
      loading: true,
      fetching: {
        otgKey: false,
        policy: false,
        json: false,
      },
      form: {
        policyid: null,
        configuration: "{}",
        device_exception_id: null,
      },
      deviceExceptionsOptions: [],
    };
  },
  validations: {
    form: {
      policyid: {},
      configuration: {
        required,
        isValidJson(jsonString) {
          try {
            const parsedJSON = JSON.parse(jsonString);
            if (parsedJSON && typeof parsedJSON === "object") return true;
          } catch (e) {
            return false;
          }
          return false;
        },
      },
      device_exception_id: {},
    },
  },
  methods: {
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapActions("customerPolicies", [
      "fetchCustomerPolicies",
      "fetchScrollCustomerPolicies",
    ]),
    ...mapActions("users", ["regenerateUserOTGKey", "fetchCustomerAccount"]),
    ...mapActions("devices", [
      "updateDeviceConfigurationTemplate",
      "udateDevicesDefaultPolicy",
      "resetDeviceConfigurationTemplate",
      "fetchDeviceConfiguration",
      "fetchDeviceConfigurationTemplate",
    ]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("deviceExceptions", ["fetchDeviceExceptions"]),
    setValidationInput(object, value) {
      this.form[`${object}`] = value;
      this.$v.form[`${object}`].$touch();
    },
    async copyInstallKey() {
      try {
        await navigator.clipboard.writeText(
          this.selectedUserAccount.install_key
        );
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Copied to clipboard."),
          duration: 2000,
          variant: "info",
        });
      } catch (err) {
        this.displayToast({
          title: this.$t("Error"),
          message: this.$t("Something went wrong."),
          duration: 2000,
          variant: "danger",
        });
      }
    },
    async onRegenerate() {
      const h = this.$createElement;

      const messageVNode = h("div", { class: ["foobar"] }, [
        h("p", { class: ["text-center"] }, [
          this.$t(
            "Once you regenerate your key, the old key is no longer valid for new OTG installations. Existing OTG 2 installations are not affected."
          ),
        ]),
      ]);

      const confirmation = await this.$bvModal.msgBoxConfirm([messageVNode], {
        title: this.$t("Regenerate OTG Key"),
        size: "lg",
        buttonSize: "sm",
        okVariant: "success",
        okTitle: this.$t("Regenerate"),
        cancelTitle: this.$t("Cancel"),
        footerClass: "p-2",
        hideHeaderClose: false,
        centered: true,
      });

      /* istanbul ignore next */
      // Reason: confirmation is returned from external modal component.
      if (confirmation) {
        this.regenerateInstallKey();
      }
    },
    async regenerateInstallKey() {
      this.fetching.otgKey = true;
      try {
        await this.regenerateUserOTGKey({ id: this.selectedUser.id });
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("OTG Key regenerated successfully"),
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetching.otgKey = false;
      }
    },
    async updateInheritedPolicy() {
      this.fetching.policy = true;
      let deviceExceptionId = this.form.device_exception_id
        ? this.form.device_exception_id
        : "";
      try {
        await this.udateDevicesDefaultPolicy({
          id: this.selectedUser.id,
          policyId: this.form.policyid,
          deviceExceptionId,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Updated"),
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetching.policy = false;
      }
    },
    async resetconfiguration() {
      this.fetching.configuration = true;
      try {
        await this.resetDeviceConfigurationTemplate({
          id: this.selectedUser.id,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Updated"),
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetching.configuration = false;
        this.enabledJsonInput = false;
        this.form.configuration = "{}";
      }
    },
    async submitConfiguration() {
      this.fetching.configuration = true;
      try {
        await this.updateDeviceConfigurationTemplate({
          id: this.selectedUser.id,
          configuration: this.form.configuration,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Updated"),
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetching.configuration = false;
        this.enabledJsonInput = false;
      }
    },
    scrollPolicy(e) {
      const { target } = e;
      let currentScrollPosition = target.scrollTop + target.scrollHeight;
      if (currentScrollPosition > this.previousScrollPosition) {
        if (
          Math.ceil(target.scrollTop) >=
            target.scrollHeight - target.offsetHeight &&
          this.dataOptions.page > this.currentPage
        ) {
          try {
            this.fetchScrollCustomerPolicies({
              id: this.selectedUser.id,
              ...this.dataOptions,
            });
          } catch (e) {
            console.log("e", e);
          } finally {
            this.currentPage = this.dataOptions.page;
            this.dataOptions.page++;
          }
        }
      }
      this.previousScrollPosition = currentScrollPosition;
    },
    changePolicy(id) {
      this.$v.form.policyid.$touch();

      let selectedPolicy = this.policiesList.filter(function (policy) {
        policy.isSelected = false;
        if (policy.value === id) {
          policy.isSelected = true;

          return policy;
        }
      });

      selectedPolicy[0].isSelected = true;
      this.selectedPolicy.text = selectedPolicy[0].text;
      this.isOpenPolicy = !this.isOpenPolicy;
      this.form.policyid = selectedPolicy[0].value;
    },
    openDropdownPolicy() {
      var _this = this;
      const closeListerner = (e) => {
        if (_this.catchOutsidePolicyClick(e, _this.$refs.menu))
          window.removeEventListener("click", closeListerner),
            (_this.isOpenPolicy = false);
      };
      window.addEventListener("click", closeListerner);
      this.isOpenPolicy = !this.isOpenPolicy;
    },
    catchOutsidePolicyClick(event, dropdown) {
      // When user clicks menu — do nothing
      if (dropdown == event.target) return false;

      // When user clicks outside of the menu — close the menu
      if (this.isOpenPolicy && dropdown != event.target) return true;
    },
  },
  computed: {
    ...mapGetters("authentication", ["selectedUser"]),
    ...mapGetters("users", ["selectedUserAccount"]),
    ...mapGetters("customerPolicies", ["customerPolicies"]),
    ...mapGetters("devices", ["selectedDevice", "templateConfig"]),
    ...mapGetters("deviceExceptions", ["deviceExceptions"]),
    policiesList: {
      get() {
        return [
          {
            value: "",
            text: this.$t("Default Policy"),
            isSelected: Object.is(this.selectedDevice.policy_id, null)
              ? true
              : false,
          },
          ...this.customerPolicies.map((policy) => {
            return {
              value: policy.id,
              text: policy.name,
              isSelected: Object.is(this.selectedDevice.policy_id, policy.id)
                ? true
                : false,
            };
          }),
        ];
      },
      set() {},
    },
  },

  watch: {
    selectedDevice() {
      this.form.policyid = this.selectedDevice.policy_id;
      this.form.configuration =
        this.templateConfig !== null
          ? JSON.stringify(this.templateConfig, null, 2)
          : "{}";
    },
    customerPolicies() {
      this.policyOptions = [
        {
          value: null,
          text: this.$t("Default Policy"),
        },
        ...this.customerPolicies.map((p) => {
          return { value: p.id, text: p.name };
        }),
      ];
    },
    deviceExceptions() {
      this.deviceExceptionsOptions = [
        {
          value: null,
          text: this.$t("None"),
          rulesCount: 1,
        },
        ...this.deviceExceptions.map((e) => {
          return { value: e.id, text: e.name, rulesCount: e.rules };
        }),
      ].filter((e) => e.rulesCount > 0);
    },
  },
  async created() {
    this.setPageTitle(this.$t("Device Configuration"));
    try {
      await Promise.all([
        this.fetchCustomerPolicies({
          id: this.selectedUser.id,
          show_default: "false",
          results: 10,
          sortDesc: [false],
        }),
        this.fetchCustomerAccount({ id: this.selectedUser.id }),
        this.fetchDeviceConfiguration({ id: this.selectedUser.id }),
        this.fetchDeviceConfigurationTemplate({ id: this.selectedUser.id }),
        this.fetchDeviceExceptions({ userId: this.selectedUser.id }),
      ]);
      this.customerPolicies.map((p) => {
        if (p.id === this.selectedDevice.policy_id) {
          this.selectedPolicy.text = p.name;
        }
      });
    } finally {
      this.loading = false;
    }
    this.form.device_exception_id = this.selectedDevice.device_exception_id;
  },
  mounted() {
    this.setPageSubtitle([
      this.$t(
        "A device is any entity that can be configured to send DNS requests to WebTitan for filtering. Devices are automatically created when WebTitan OTG 2 is installed on an endpoint like a remote laptop or Chromebook. Changes made here are applied to all devices, or you can edit a single device from Environment > Devices."
      ),
    ]);
  },
};
</script>

<style lang="scss" scoped>
.t-collapse {
  border-bottom: none;
}
.dropDownMenuWrapperPolicy {
  position: relative;
  width: 100%;
  height: calc(1.5em + 0.75rem + 2px);
  border-radius: 0.25rem;
  background: white;
  border: 1px solid #ced4da;
  color: #495057;
  margin-bottom: 2rem;
  margin-top: 5px;
  z-index: 1;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  background-clip: padding-box;

  .disabled {
    background: #e9ecef !important;
  }
  .dropDownMenuButton {
    border: none;
    font-size: inherit;
    background: none;
    outline: none;
    border-radius: 4px;
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    padding: 0 70px 0 15px;
    margin: 0;
    line-height: 1;
    width: 100%;
    height: 100%;
    z-index: 2;
    cursor: pointer;
  }

  .iconWrapper {
    width: 25px;
    height: 25px;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translate(0, -50%);
    z-index: 1;
  }
  .dropdownMenu {
    position: absolute;
    top: 50%;
    width: 100%;
    min-width: 300px;
    height: auto;
    left: 0;
    max-height: 150px;

    border-radius: 8px;
    border: 1px solid #eee;
    box-shadow: 10px 10px 0 0 rgba(black, 0.03);
    background: white;
    padding: 10px 30px;
    animation: menu 0.3s ease forwards;
    overflow: scroll;

    &__list {
      position: relative;
      div {
        font-weight: normal;
        display: block;
        white-space: nowrap;
        min-height: 1.2em;
        position: relative;

        cursor: pointer;
        &:hover {
          font-weight: bolder;
        }
        button {
          width: 100%;
          text-align: left;
          display: flex;
        }
        .item {
          position: relative;
        }
        .icon-wrap {
          position: absolute;
          left: -20px;
          top: -1px;
        }
      }
      .disabled {
        opacity: 0.3;
        cursor: default;
        &:hover {
          font-weight: normal;
        }
      }
    }
    .isSelected {
      font-weight: 500;
    }
    .notSelect {
      background-color: transparent;
    }

    .option {
      width: 100%;
      border-bottom: 1px solid #eee;
      padding: 20px 0;
      cursor: pointer;
      position: relative;
      z-index: 2;

      &:last-child {
        border-bottom: 0;
      }

      * {
        color: inherit;
        text-decoration: none;
        background: none;
        border: 0;
        padding: 0;
        outline: none;
        cursor: pointer;
      }
    }

    .desc {
      opacity: 0.5;
      display: block;
      width: 100%;
      font-size: 14px;
      margin: 3px 0 0 0;
      cursor: default;
    }
  }

  @keyframes menu {
    from {
      transform: translate3d(0, 30px, 0);
    }
    to {
      transform: translate3d(0, 20px, 0);
    }
  }
}
</style>
